import { Cause, Result, Schema } from 'effect';
import { AsyncResult, Atom } from 'effect/reactivity';

/** better-auth's session store as an app's client exposes it
 * (`authClient.$store.atoms.session`) — the store `useSession` renders from.
 * better-auth types its stores as `any`, so values are decoded here. */
export type AuthSessionStore = {
  readonly get: () => unknown;
  readonly listen: (listener: (value: unknown) => void) => () => void;
};

class MissingSessionStoreError extends Schema.TaggedError<MissingSessionStoreError>()(
  'MissingSessionStoreError',
  { message: Schema.String },
) {}

const SessionStoreState = Schema.Struct({
  isPending: Schema.Boolean,
  data: Schema.NullOr(
    Schema.Struct({ user: Schema.Struct({ id: Schema.String }) }),
  ),
});
const decodeSessionStoreState = Schema.decodeUnknownResult(SessionStoreState);

const settledUserLoggedIn = (
  value: unknown,
): AsyncResult.AsyncResult<boolean> | undefined => {
  const state = decodeSessionStoreState(value);
  if (Result.isFailure(state))
    return AsyncResult.failure(Cause.die(state.failure));
  if (state.success.data !== null) return AsyncResult.success(true);
  return state.success.isPending ? undefined : AsyncResult.success(false);
};

/**
 * Whether a user is signed in, read from the same store `useSession` renders
 * from, so it never disagrees with the UI and adds no request. A session in
 * the store answers at once, including one mobile restores from its cache
 * while `/get-session` is still pending; with none, Initial until that fetch
 * settles — signed in, 401, or failed — then the latest settled answer; a
 * later refetch keeps the previous answer until it settles.
 */
export const makeUserLoggedInAtom = (
  readSessionStore: () => AuthSessionStore | undefined,
) =>
  Atom.make((get): AsyncResult.AsyncResult<boolean> => {
    const sessionStore = readSessionStore();
    if (!sessionStore)
      return AsyncResult.failure(
        Cause.die(
          MissingSessionStoreError.make({
            message: 'better-auth client exposes no session store',
          }),
        ),
      );
    get.addFinalizer(
      sessionStore.listen((value) => {
        const answer = settledUserLoggedIn(value);
        if (answer) get.setSelf(answer);
      }),
    );
    return settledUserLoggedIn(sessionStore.get()) ?? AsyncResult.initial();
  });
